import axios from 'axios';
import { io } from 'socket.io-client';
import {
  API_URLS,
  BASE_URL,
  SOCKET_URL,
  getTeamImageUrl,
  rewriteLocalMediaInText,
  rewriteLocalMediaUrl,
} from '../constants/config';
import { authorizedPost } from './stadiumsApi';
import { parseMoscowDateTime } from './matchesApi';
import { FALLBACK_SOLID_UNDERLOZHKI, SOLID_UNDERLOZHKA_KEYS } from './socialPicturesApi';

const apiRoot = () => String(BASE_URL.url || '').replace(/\/+$/, '');

/**
 * URL для io(...). Никогда не передаём '/api' — socket.io примет это как namespace
 * на origin SPA (:8080), а сокет живёт на API (:3000).
 */
export const resolveTabloSocketUrl = () => {
  const explicit = String(SOCKET_URL || '').trim().replace(/\/+$/, '');
  if (explicit) return explicit;

  const base = apiRoot();
  if (/^https?:\/\//i.test(base)) {
    try {
      return new URL(base).origin;
    } catch (_err) {
      return base;
    }
  }

  if (typeof window === 'undefined' || !window.location) return undefined;
  const { protocol, hostname, port } = window.location;
  // Proto: админка на :8080, API+socket.io на :3000 без nginx Upgrade
  if (port === '8080') {
    return `${protocol}//${hostname}:3000`;
  }
  // CRA :3001 + setupProxy /socket.io, или nginx на одном origin
  return window.location.origin;
};

export const TABLO_DEFAULT_SCENE = 'preview';

/** Базовый слой счёта — включается отдельно, не вместо pack-сцены. */
export const TABLO_SCORE_SCENE = 'score';

/** Сцены, которые не показываем в селекте админки (остаются aliases для normalize). */
export const TABLO_HIDDEN_SCENES = new Set(['matchday', 'event']);

export const TABLO_FALLBACK_SCENES = [
  { key: 'score', label: 'Табло (счёт)', labelEn: 'Scoreboard', layout: 'scoreboard_tl', pack: 'score', always_base: true },
  { key: 'preview', label: 'Превью матча', labelEn: 'Match preview', layout: 'match_preview_overlay', pack: 'preview' },
  { key: 'final', label: 'Итог матча', labelEn: 'Match final', layout: 'match_final_overlay', pack: 'final' },
  { key: 'result', label: 'Подробный результат', labelEn: 'Result', layout: 'match_result_overlay', pack: 'result' },
  { key: 'lineups', label: 'Составы', labelEn: 'Lineups', layout: 'match_lineups_overlay', pack: 'lineups' },
  { key: 'pitch', label: 'Составы на поле', labelEn: 'Lineups on pitch', layout: 'match_lineups_pitch', pack: 'pitch' },
  { key: 'stats', label: 'Статистика матча', labelEn: 'Match stats', layout: 'match_stats_overlay', pack: 'stats' },
];

/** Группы pack-сцен в админке. Одна сцена может быть в нескольких группах. */
export const TABLO_SCENE_GROUPS = [
  { id: 'prematch', labelKey: 'tabloGroupPrematch', keys: ['preview', 'lineups', 'pitch'] },
  { id: 'break', labelKey: 'tabloGroupBreak', keys: ['result', 'stats'] },
  { id: 'fulltime', labelKey: 'tabloGroupFulltime', keys: ['stats', 'result', 'final'] },
];

/** Старые picture_type / aliases → video-pack scene. */
const SCENE_ALIASES = {
  score: 'score',
  scoreboard: 'score',
  scoreboard_tl: 'score',
  matchday: 'matchday',
  matchday_cover: 'matchday',
  matchday_cover_v2: 'matchday',
  preview_v2: 'matchday',
  preview: 'preview',
  final: 'final',
  match_final: 'final',
  fulltime_preview: 'final',
  ended: 'final',
  postmatch: 'final',
  detailed_result_v2: 'result',
  result: 'result',
  match_lineups_v2: 'lineups',
  match_subs_v2: 'lineups',
  lineups: 'lineups',
  pitch: 'pitch',
  match_lineups_pitch: 'pitch',
  stats: 'stats',
  match_stats_v2: 'stats',
  event: 'event',
  match_cover_goal: 'event',
};

export const normalizeTabloScene = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return TABLO_DEFAULT_SCENE;
  const mapped = SCENE_ALIASES[raw] || SCENE_ALIASES[raw.toLowerCase()];
  if (mapped) return mapped;
  if (TABLO_FALLBACK_SCENES.some((item) => item.key === raw)) return raw;
  return TABLO_DEFAULT_SCENE;
};

export const isTabloScoreScene = (scene) => normalizeTabloScene(scene) === TABLO_SCORE_SCENE;

/**
 * Pack-сцена поверх табло.
 * null / score / off — пак выключен, на экране только табло.
 */
export const resolveTabloPackScene = (scene) => {
  if (scene == null || scene === '' || scene === 'none' || scene === 'off') return null;
  const key = normalizeTabloScene(scene);
  if (key === TABLO_SCORE_SCENE) return null;
  return key;
};

export const getTabloStyles = (dispatch) => (
  authorizedPost(dispatch, API_URLS.getTabloStyles, {})
);

/**
 * @param {{ matchId: number, scene?: string, scoreboard?: boolean, customBg?: object|null }} opts
 */
export const setTabloSession = (dispatch, {
  matchId,
  scene,
  scoreboard,
  customBg,
} = {}) => {
  const packScene = resolveTabloPackScene(scene);
  const body = {
    match_id: Number(matchId),
    // Без pack-сцены сервер хранит scene=score — это «только табло»
    scene: packScene || TABLO_SCORE_SCENE,
  };
  if (typeof scoreboard === 'boolean') {
    body.scoreboard = scoreboard;
    body.scoreboard_visible = scoreboard;
  }
  if (customBg && typeof customBg === 'object') {
    body.customBg = {
      ...customBg,
      ...(typeof scoreboard === 'boolean' ? { scoreboard_visible: scoreboard } : {}),
    };
  } else if (typeof scoreboard === 'boolean') {
    // Сервер хранит только style_json — флаг едет внутри customBg
    body.customBg = { scoreboard_visible: scoreboard };
  }
  return authorizedPost(dispatch, API_URLS.setTabloSession, body);
};

/** Сохранить solid-схему табло для лиги+сезона матча (без открытия эфира). */
export const setTabloLeagueStyle = (dispatch, { matchId, customBg } = {}) => (
  authorizedPost(dispatch, API_URLS.setTabloLeagueStyle, {
    match_id: Number(matchId),
    customBg: customBg && typeof customBg === 'object' ? customBg : undefined,
  })
);

export const getTabloState = (dispatch, matchId) => (
  authorizedPost(dispatch, API_URLS.getTabloState, { match_id: Number(matchId) })
);

/**
 * Только слои (+ clock/style/scene). Auth: match_id; публично: overlay_key.
 * customBg/scene — для превью без открытия эфира (пересборка плашек).
 */
export const getTabloLayers = (dispatch, {
  matchId,
  overlayKey,
  customBg,
  scene,
  scoreboard,
} = {}) => {
  const body = {};
  // scene=score валиден здесь: нужен layers.scoreboard, не pack-сцена сессии
  if (scene) body.scene = normalizeTabloScene(scene);
  if (typeof scoreboard === 'boolean') {
    body.scoreboard = scoreboard;
    body.scoreboard_visible = scoreboard;
  }
  if (customBg === null) body.customBg = null;
  else if (customBg && typeof customBg === 'object') body.customBg = customBg;

  if (overlayKey) {
    return axios.post(`${apiRoot()}${API_URLS.getTabloLayers}`, {
      overlay_key: String(overlayKey),
      ...body,
    }, { headers: { Accept: 'application/json' } });
  }
  return authorizedPost(dispatch, API_URLS.getTabloLayers, {
    match_id: Number(matchId),
    ...body,
  });
};

export const closeTabloSession = (dispatch, matchId) => (
  authorizedPost(dispatch, API_URLS.closeTabloSession, { match_id: Number(matchId) })
);

export const rotateTabloOverlayKey = (dispatch, matchId) => (
  authorizedPost(dispatch, API_URLS.rotateTabloOverlayKey, { match_id: Number(matchId) })
);

export const fetchTabloOverlay = async (overlayKey) => {
  const key = String(overlayKey || '').trim();
  if (!key) throw new Error('overlay_key required');
  const response = await axios.get(`${apiRoot()}${API_URLS.tabloOverlay}/${encodeURIComponent(key)}`, {
    headers: { Accept: 'application/json' },
  });
  return response.data;
};

export const extractTabloSession = (payload = {}) => {
  const session = payload.session || payload;
  if (!session || typeof session !== 'object') return null;
  const matchId = Number(session.match_id);
  const overlayKey = session.overlay_key || null;
  if (!Number.isFinite(matchId) || matchId <= 0) return null;
  if (!overlayKey && payload.session_active === false) return null;
  return {
    match_id: matchId,
    scene: normalizeTabloScene(session.scene || session.picture_type),
    picture_type: normalizeTabloScene(session.picture_type || session.scene),
    overlay_key: overlayKey,
    updated_at: session.updated_at || null,
    created_at: session.created_at || null,
  };
};

export const isTabloClosedPayload = (payload) => Boolean(
  payload
  && typeof payload === 'object'
  && (payload.closed === true || payload.reason === 'closed' || payload.reason === 'rotated')
);

export const extractTabloScenes = (payload = {}) => {
  const list = Array.isArray(payload.scenes)
    ? payload.scenes
    : (Array.isArray(payload.styles) ? payload.styles : []);
  const normalized = list.map((item) => {
    const key = normalizeTabloScene(item.key || item.scene || item.picture_type || item.id);
    return {
      key,
      label: item.label || item.name || key,
      labelEn: item.labelEn || item.label_en || item.label || key,
      layout: item.layout || '',
      pack: item.pack || key,
      always_base: Boolean(item.always_base),
      auto_on_event: Boolean(item.auto_on_event),
    };
  }).filter((item) => item.key && !TABLO_HIDDEN_SCENES.has(item.key));

  if (!normalized.length) return TABLO_FALLBACK_SCENES.map((item) => ({ ...item }));

  // Unique by scene key, prefer known video-pack order
  const byKey = new Map();
  normalized.forEach((item) => {
    if (!byKey.has(item.key)) byKey.set(item.key, item);
  });
  const ordered = TABLO_FALLBACK_SCENES
    .map((fallback) => byKey.get(fallback.key) || fallback)
    .concat([...byKey.values()].filter((item) => !TABLO_FALLBACK_SCENES.some((f) => f.key === item.key)))
    .filter((item) => !TABLO_HIDDEN_SCENES.has(item.key));
  return ordered;
};

const isBlankOrWhiteAccent = (value) => {
  const raw = String(value || '').trim().toLowerCase().replace(/\s+/g, '');
  if (!raw) return true;
  if (raw === '#fff' || raw === '#ffffff' || raw === '#ffffffff') return true;
  if (raw === 'white' || raw === 'transparent') return true;
  if (raw === 'rgb(255,255,255)' || raw === 'rgba(255,255,255,1)') return true;
  // 8-digit hex with full alpha
  if (/^#([0-9a-f]{6})ff$/.test(raw) && RegExp.$1 === 'ffffff') return true;
  return false;
};

/** Старые pattern/*_long → ближайший solid для табло. */
const PATTERN_TO_SOLID = {
  afl_long: 'blue',
  blue_long: 'blue',
  green_long: 'green',
  brown_long: 'brown',
  yellow_long: 'yellow',
  red_long: 'red',
  purple_long: 'purple',
  nbsl_long: 'blue',
  spb_long: 'blue',
  superleague_long: 'blue',
};

/** Нормализует ключ подложки к solid (blue/green/…). */
export const normalizeTabloSolidKey = (themeKey) => {
  const raw = String(themeKey || '').trim().toLowerCase();
  if (!raw) return null;
  if (SOLID_UNDERLOZHKA_KEYS.includes(raw)) return raw;
  if (PATTERN_TO_SOLID[raw]) return PATTERN_TO_SOLID[raw];
  if (raw.endsWith('_long')) {
    const base = raw.replace(/_long$/, '');
    if (SOLID_UNDERLOZHKA_KEYS.includes(base)) return base;
  }
  return null;
};

/** Только solid-цвета для пикера табло; старые pattern/*_long мапим в solid. */
export const resolveTabloSolidTheme = (themeKey, catalog = []) => {
  const list = Array.isArray(catalog) && catalog.length
    ? catalog
    : FALLBACK_SOLID_UNDERLOZHKI;
  const key = normalizeTabloSolidKey(themeKey) || 'blue';
  return list.find((item) => item.key === key)
    || FALLBACK_SOLID_UNDERLOZHKI.find((item) => item.key === key)
    || list.find((item) => SOLID_UNDERLOZHKA_KEYS.includes(item.key))
    || list[0]
    || FALLBACK_SOLID_UNDERLOZHKI[0];
};

/**
 * Акцент для табло: привязан к solid-подложке.
 * Белый / chrome-accent из стиля лиги и accent от старых *_long игнорируем.
 */
export const resolveTabloSolidAccent = (style = {}, catalog = [], opts = {}) => {
  const solid = resolveTabloSolidTheme(style.theme || style.underlozhka, catalog);
  const rawKey = String(style.theme || style.underlozhka || '').trim().toLowerCase();
  const wasSolid = SOLID_UNDERLOZHKA_KEYS.includes(rawKey);
  const accent = style.accent || style.accent_color || style.primary;
  // Всегда брать accent подложки: лига часто отдаёт #fff / pattern-accent
  if (opts.forceSolid || !wasSolid || isBlankOrWhiteAccent(accent)) {
    return solid?.accent || '#1550F5';
  }
  return String(accent).trim();
};

const mapTabloUnderlozhka = (item) => ({
  key: item.key || item.theme || item.id,
  label: item.label || item.name || item.key,
  type: item.type || (SOLID_UNDERLOZHKA_KEYS.includes(String(item.key || item.theme || '')) ? 'solid' : item.type),
  accent: item.accent || item.accent_color || '#1550F5',
  backgroundColor: item.backgroundColor || item.background_color || item.background || '#2A3A8F',
  preview: item.preview || item.image || '',
  background: item.background || item.bg || '',
});

const isSolidTabloUnderlozhka = (item) => {
  const key = String(item?.key || '').trim();
  if (!key) return false;
  if (item.type === 'solid' || SOLID_UNDERLOZHKA_KEYS.includes(key)) return true;
  if (item.type === 'pattern' || key.endsWith('_long')) return false;
  return SOLID_UNDERLOZHKA_KEYS.includes(key);
};

/** Только новые solid-цвета (без pattern / *_long). */
export const extractTabloUnderlozhki = (payload = {}) => {
  const groups = payload?.groups && typeof payload.groups === 'object' ? payload.groups : {};
  const fromGroup = Array.isArray(groups.solid) ? groups.solid : [];
  const fromFlat = Array.isArray(payload.underlozhki) ? payload.underlozhki : [];
  const source = fromGroup.length ? fromGroup : fromFlat;
  const solid = source
    .map(mapTabloUnderlozhka)
    .filter(isSolidTabloUnderlozhka);

  if (!solid.length) {
    return FALLBACK_SOLID_UNDERLOZHKI.map((item) => ({
      key: item.key,
      label: item.label,
      type: 'solid',
      accent: item.accent,
      backgroundColor: item.backgroundColor,
      preview: item.preview || '',
      background: item.background || '',
    }));
  }

  const byKey = new Map();
  solid.forEach((item) => {
    if (!byKey.has(item.key)) byKey.set(item.key, item);
  });
  return SOLID_UNDERLOZHKA_KEYS
    .map((key) => byKey.get(key))
    .filter(Boolean)
    .concat([...byKey.values()].filter((item) => !SOLID_UNDERLOZHKA_KEYS.includes(item.key)));
};

/** @deprecated use extractTabloScenes */
export const extractTabloStyles = extractTabloScenes;

const LAYER_KEYS = ['scoreboard', 'preview', 'final', 'matchday', 'result', 'lineups', 'pitch', 'stats', 'event'];

/**
 * Live scoreboard HTML embeds an empty .event-card shell. Old server templates
 * only hide it for .is-score-only, so under .is-live-scoreboard the empty plaque
 * stays visible from session start. Strip/hide it on the client as a safety net.
 *
 * Важно: общий шаблон всегда содержит CSS `.page.is-live-scoreboard .event-card`,
 * даже у карточки события (`is-event-card-only`). Режем только когда на .page
 * реально висит класс is-live-scoreboard — иначе вырезаем саму сцену события.
 */
export const sanitizeLiveScoreboardHtml = (html) => {
  let out = String(html || '');
  if (!out || !/event-card/i.test(out)) return out;
  // class на .page, не упоминание в CSS
  if (!/<div class="page[^"]*\bis-live-scoreboard\b/i.test(out)) return out;
  if (!/\.page\.is-live-scoreboard\s+\.event-card\s*\{[^}]*display\s*:\s*none/i.test(out)) {
    out = out.replace(
      /<\/style>/i,
      '\n    .page.is-live-scoreboard .event-card { display: none !important; }\n  </style>'
    );
  }
  return out.replace(
    /\n\s*<div class="event-card">[\s\S]*?\n\s*<\/div>(?=\n\s*<\/div>\n\s*<\/body>)/,
    ''
  );
};

export const SCENE_TO_LAYER = {
  score: 'scoreboard',
  preview: 'preview',
  final: 'final',
  matchday: 'matchday',
  result: 'result',
  lineups: 'lineups',
  pitch: 'pitch',
  stats: 'stats',
  event: 'event',
};

/** Откуда въезжает кадр при смене сцены (чисто UI overlay/превью). */
export const TABLO_SCENE_ENTER_FROM = {
  result: 'bottom',
  lineups: 'bottom',
  pitch: 'bottom',
  stats: 'bottom',
  event: 'right',
  score: 'fade',
  preview: 'fade',
  final: 'fade',
  matchday: 'fade',
};

export const getTabloSceneEnterFrom = (scene) => (
  TABLO_SCENE_ENTER_FROM[normalizeTabloScene(scene)] || 'fade'
);

export const TABLO_SCENE_TRANSITION_MS = 560;

const LAYER_KEY_ALIASES = {
  score: 'scoreboard',
  scoreboard: 'scoreboard',
  scoreboard_tl: 'scoreboard',
  preview: 'preview',
  final: 'final',
  matchday: 'matchday',
  result: 'result',
  lineups: 'lineups',
  pitch: 'pitch',
  stats: 'stats',
  event: 'event',
};

const maybeSanitizeLayerHtml = (key, html) => {
  const rewritten = rewriteLocalMediaInText(html);
  // sanitize только для scoreboard: event/result шарят тот же CSS-шаблон
  if (key === 'scoreboard') return sanitizeLiveScoreboardHtml(rewritten);
  return rewritten;
};

const normalizeLayerItem = (item, layerKey) => {
  if (typeof item === 'string' && item.trim()) {
    return {
      type: 'html',
      html: maybeSanitizeLayerHtml(layerKey, item),
      url: '',
      width: 1920,
      height: 1080,
    };
  }
  if (!item || typeof item !== 'object') return null;
  const html = typeof item.html === 'string'
    ? maybeSanitizeLayerHtml(layerKey, item.html)
    : '';
  const url = item.url ? rewriteLocalMediaUrl(item.url) : '';
  if (!html && !url) return null;
  return {
    type: item.type || (html ? 'html' : (url ? 'png' : 'html')),
    html,
    url,
    width: Number(item.width) || 1920,
    height: Number(item.height) || 1080,
  };
};

export const normalizeTabloLayers = (layers) => {
  if (!layers || typeof layers !== 'object') return {};
  const next = {};
  Object.keys(layers).forEach((rawKey) => {
    const key = LAYER_KEY_ALIASES[rawKey] || LAYER_KEY_ALIASES[String(rawKey).toLowerCase()];
    if (!key || !LAYER_KEYS.includes(key)) return;
    const normalized = normalizeLayerItem(layers[rawKey], key);
    if (!normalized) return;
    // Не затираем уже найденный html пустым alias-дублем
    if (next[key]?.html && !normalized.html) return;
    next[key] = normalized;
  });
  return next;
};

/** Slim socket/HTTP updates: keep previous frames unless next has html/url. */
export const mergeTabloLayers = (prevLayers, nextLayers) => {
  if (!nextLayers || !Object.keys(nextLayers).length) return prevLayers || {};
  const merged = { ...(prevLayers || {}) };
  Object.keys(nextLayers).forEach((key) => {
    const next = nextLayers[key];
    if (!next) return;
    if (next.html || next.url) merged[key] = next;
    else if (!merged[key]) merged[key] = next;
  });
  return merged;
};

export const mergeTabloLayerUrls = (prevUrls, nextUrls) => {
  if (!nextUrls || !Object.keys(nextUrls).length) return prevUrls || {};
  return { ...(prevUrls || {}), ...nextUrls };
};

/** Merge overlay/socket state so pulse event doesn't wipe pack/scoreboard frames. */
export const mergeTabloOverlayState = (prev, next) => {
  if (!next || typeof next !== 'object') return prev || next;
  if (!prev || typeof prev !== 'object') return next;
  if (next.closed && !next.match && !next.layers) return next;
  return {
    ...prev,
    ...next,
    style: next.style && typeof next.style === 'object'
      ? next.style
      : (prev.style || {}),
    match: next.match || prev.match || null,
    clock: preferTabloClock(next.clock, prev.clock),
    layers: mergeTabloLayers(prev.layers, next.layers),
    layer_urls: mergeTabloLayerUrls(prev.layer_urls, next.layer_urls),
    // empty arrays from slim payload must not wipe roster-derived UI if we keep them
    events: Array.isArray(next.events) && next.events.length
      ? next.events
      : (prev.events || []),
    lineups: next.lineups && Object.keys(next.lineups || {}).length
      ? next.lineups
      : (prev.lineups || {}),
    applications: Array.isArray(next.applications) && next.applications.length
      ? next.applications
      : (prev.applications || []),
    statistics: Array.isArray(next.statistics) && next.statistics.length
      ? next.statistics
      : (prev.statistics || []),
  };
};

export const normalizeTabloLayerUrls = (urls) => {
  if (!urls || typeof urls !== 'object') return {};
  const next = {};
  Object.keys(urls).forEach((rawKey) => {
    const key = LAYER_KEY_ALIASES[rawKey] || LAYER_KEY_ALIASES[String(rawKey).toLowerCase()];
    if (!key || !LAYER_KEYS.includes(key) || !urls[rawKey]) return;
    next[key] = rewriteLocalMediaUrl(String(urls[rawKey]));
  });
  return next;
};

/** Достать scoreboard из сырого payload layers (до/вместо extractTabloState). */
export const pickTabloScoreboardLayer = (payload = {}) => {
  if (!payload || typeof payload !== 'object') return null;
  const root = payload.state && typeof payload.state === 'object' ? payload.state : payload;
  const rawLayers = root.layers || payload.layers || {};
  const fromMap = normalizeLayerItem(
    rawLayers.scoreboard
    || rawLayers.score
    || rawLayers.scoreboard_tl
  );
  if (fromMap) return fromMap;

  const scene = normalizeTabloScene(root.scene || payload.scene);
  if (scene === TABLO_SCORE_SCENE) {
    return normalizeLayerItem(
      root.layer
      || payload.layer
      || (typeof root.html === 'string' ? root : null)
      || (typeof payload.html === 'string' ? payload : null)
    );
  }
  return null;
};

/** Сколько держим карточку события (ЖК/КК) поверх pack-сцены, затем сами закрываем. */
export const TABLO_EVENT_CARD_MS = 6000;

/** Сколько держим pulse «подробный результат» после гола (сервер: событие 6с + пауза 2с). */
export const TABLO_RESULT_PULSE_MS = 6000;

/** Длительность result-pulse: с сервера (pages×hold), иначе дефолт. */
export const resolveTabloResultPulseMs = (state) => {
  const ms = Number(state?.layers?.result?.pulse_ms);
  if (Number.isFinite(ms) && ms > 0) return ms;
  return TABLO_RESULT_PULSE_MS;
};

/**
 * Pack-слой (не scoreboard, не event-pulse).
 * Карточка события — отдельный оверлей в TabloBoard, не подмена pack-сцены
 * (иначе въезжает второй экземпляр табло из layers.event).
 */
export const resolveTabloLayerKey = (state) => {
  const scene = resolveTabloPackScene(state?.scene);
  if (!scene || scene === 'event') return null;
  return SCENE_TO_LAYER[scene] || null;
};

/** Есть ли готовый кадр карточки события для pulse-оверлея. */
export const hasTabloEventLayer = (state) => {
  const eventLayer = state?.layers?.event;
  const eventUrl = state?.layer_urls?.event;
  return Boolean((eventLayer && eventLayer.html) || eventUrl);
};

/** Видимость базового табло: явно из state/style, иначе fallback. */
export const resolveTabloScoreVisible = (state, fallback = true) => {
  if (state && typeof state === 'object') {
    if (typeof state.scoreboard_visible === 'boolean') return state.scoreboard_visible;
    if (typeof state.scoreboard === 'boolean') return state.scoreboard;
    if (typeof state.scoreboard_on === 'boolean') return state.scoreboard_on;
    if (typeof state.style?.scoreboard_visible === 'boolean') return state.style.scoreboard_visible;
  }
  return fallback;
};

/** Авто-плашки событий (карточка / подробный результат). Нет поля → включено. */
export const resolveTabloEventPulses = (state, fallback = true) => {
  if (state && typeof state === 'object') {
    if (typeof state.event_pulses === 'boolean') return state.event_pulses;
    if (typeof state.style?.event_pulses === 'boolean') return state.style.event_pulses;
  }
  return fallback;
};

export const resolveTabloLayerUrl = (pathOrUrl) => {
  const raw = String(pathOrUrl || '').trim();
  if (!raw) return '';
  // localhost / 127.0.0.1 / LAN → BASE_URL; публичные http(s) оставляем
  if (/^https?:\/\//i.test(raw) || raw.startsWith('//')) {
    return rewriteLocalMediaUrl(raw);
  }
  if (raw.startsWith('/')) return rewriteLocalMediaUrl(raw);
  return rewriteLocalMediaUrl(`/${raw}`);
};

/** Готовый payload для iframe: srcdoc или src (вариант B). */
export const getTabloLayerView = (state, layerKey) => {
  const key = layerKey || resolveTabloLayerKey(state);
  const layer = state?.layers?.[key];
  if (layer?.html) {
    return {
      key,
      mode: 'srcdoc',
      // Ещё раз: HTML мог прийти мимо normalizeTabloLayers
      html: maybeSanitizeLayerHtml(key, layer.html),
      width: layer.width || 1920,
      height: layer.height || 1080,
    };
  }
  const url = state?.layer_urls?.[key];
  if (url) {
    return {
      key,
      mode: 'src',
      src: resolveTabloLayerUrl(url),
      width: layer?.width || 1920,
      height: layer?.height || 1080,
    };
  }
  return null;
};

export const extractTabloState = (payload = {}) => {
  if (!payload || typeof payload !== 'object') return null;

  if (isTabloClosedPayload(payload) && !payload.match) {
    return {
      closed: true,
      reason: payload.reason || 'closed',
      match_id: Number(payload.match_id) || null,
      overlay_key: payload.overlay_key || null,
      new_overlay_key: payload.new_overlay_key || null,
      updated_at: payload.updated_at || null,
    };
  }

  const state = payload.state && typeof payload.state === 'object'
    && (payload.state.match || payload.state.closed || payload.state.match_id
      || payload.state.scene || payload.state.layers || payload.state.layer_urls)
    ? payload.state
    : payload;

  if (isTabloClosedPayload(state) && !state.match) {
    return {
      closed: true,
      reason: state.reason || 'closed',
      match_id: Number(state.match_id) || null,
      overlay_key: state.overlay_key || null,
      new_overlay_key: state.new_overlay_key || null,
      updated_at: state.updated_at || null,
    };
  }

  if (!state.match && !state.match_id && !state.scene && !state.layers && !state.layer_urls) {
    return state.session ? null : state;
  }

  const scene = normalizeTabloScene(
    state.scene
    || state.picture_type
    || state.scene_meta?.key
  );

  const hasScoreFlag = typeof state.scoreboard_visible === 'boolean'
    || typeof state.scoreboard === 'boolean'
    || typeof state.scoreboard_on === 'boolean';
  const hasEventPulsesFlag = typeof state.event_pulses === 'boolean'
    || typeof state.style?.event_pulses === 'boolean';

  return {
    closed: Boolean(state.closed),
    reason: state.reason || null,
    match_id: Number(state.match_id || state.match?.id) || null,
    overlay_key: state.overlay_key || null,
    scene,
    picture_type: scene,
    scene_meta: state.scene_meta || null,
    base_layer: state.base_layer || 'scoreboard_tl',
    ...(hasScoreFlag ? { scoreboard_visible: resolveTabloScoreVisible(state, false) } : {}),
    event_pulses: hasEventPulsesFlag
      ? resolveTabloEventPulses(state, true)
      : true,
    style: state.style && typeof state.style === 'object' ? state.style : {},
    style_source: state.style_source || null,
    // Не подставляем match: null — иначе getTabloLayers затирает матч из getTabloState
    ...(state.match ? { match: state.match } : {}),
    clock: state.clock && typeof state.clock === 'object' ? state.clock : null,
    events: Array.isArray(state.events) ? state.events : [],
    active_event: state.active_event || null,
    lineups: state.lineups && typeof state.lineups === 'object' ? state.lineups : {},
    applications: Array.isArray(state.applications) ? state.applications : [],
    statistics: Array.isArray(state.statistics) ? state.statistics : [],
    layers: normalizeTabloLayers(state.layers),
    layer_urls: normalizeTabloLayerUrls(state.layer_urls),
    pulse: state.pulse || null,
    updated_at: state.updated_at || null,
    session_active: payload.session_active,
  };
};

export const buildTabloOverlayPageUrl = (overlayKey, origin = (typeof window !== 'undefined' ? window.location.origin : '')) => {
  const key = String(overlayKey || '').trim();
  if (!key) return '';
  return `${String(origin).replace(/\/+$/, '')}/tablo/overlay/${encodeURIComponent(key)}`;
};

export const pickTabloStyleColors = (style = {}) => ({
  theme: style.theme || style.underlozhka || 'blue',
  accent: style.accent || style.accent_color || style.primary || '#1550F5',
  background: style.backgroundColor || style.background || style.bg || '#0B0B0B',
  text: style.textColor || style.color || style.text || '#FFFFFF',
  underlozhka: style.underlozhka || style.theme || 'blue',
  backgroundImage: typeof style.background === 'string' && style.background.startsWith('/')
    ? style.background
    : (style.backgroundImage || ''),
});

export const buildTabloCustomBg = ({
  theme,
  accent,
  stadiumBackground,
  logoCaption,
  eventPulses,
} = {}) => {
  const body = {};
  if (theme) body.theme = theme;
  if (accent) body.accent = accent;
  if (stadiumBackground) body.stadiumBackground = stadiumBackground;
  if (logoCaption) body.logoCaption = logoCaption;
  if (typeof eventPulses === 'boolean') body.event_pulses = eventPulses;
  return Object.keys(body).length ? body : undefined;
};

/** Формат часов на табло: 05:34 */
export const formatTabloClockDisplay = (minute, second) => {
  const mm = String(Math.max(0, Number(minute) || 0)).padStart(2, '0');
  const ss = String(Math.max(0, Math.min(59, Number(second) || 0))).padStart(2, '0');
  return `${mm}:${ss}`;
};

const clockTotalSec = (clock) => (
  (Number(clock?.minute) || 0) * 60 + (Number(clock?.second) || 0)
);

/**
 * Догоняем snapshot clock по server_now (сек с момента расчёта на сервере).
 * Источник истины — start_in на бэке; клиент только интерполирует между апдейтами.
 */
export const applyTabloClockSkew = (clock, nowMs = Date.now()) => {
  if (!clock || typeof clock !== 'object' || !clock.running) return clock;
  const serverAt = parseMoscowDateTime(clock.server_now);
  if (!serverAt) return clock;
  const skewSec = Math.max(0, Math.floor((nowMs - serverAt.getTime()) / 1000));
  if (skewSec <= 0) {
    return {
      ...clock,
      display: formatTabloClockDisplay(clock.minute, clock.second),
    };
  }
  const total = clockTotalSec(clock) + skewSec;
  const minute = Math.max(0, Math.floor(total / 60));
  const second = Math.max(0, total % 60);
  return {
    ...clock,
    minute,
    second,
    display: formatTabloClockDisplay(minute, second),
  };
};

/**
 * Серверный clock — источник истины. Локальный тик только маскирует лаг сети.
 * Оставляем защиту от ложного 00:00 (старый баг парсинга start_in на UTC-сервере).
 */
export const preferTabloClock = (nextClock, prevClock) => {
  if (!nextClock || typeof nextClock !== 'object') return prevClock || null;
  const skewed = applyTabloClockSkew(nextClock);
  if (!prevClock || typeof prevClock !== 'object') return skewed;

  const nextTotal = clockTotalSec(skewed);
  const prevTotal = clockTotalSec(prevClock);
  const nextPhase = String(skewed.phase || '');
  const intentionalStop = nextPhase === 'ht' || nextPhase === 'ft' || nextPhase === 'pre_et'
    || nextPhase === 'et_ht' || nextPhase === 'pre';

  if (intentionalStop && nextPhase !== 'pre') return skewed;

  // Ложный 00:00 при живом матче (до фикса wall-time на сервере / гонка)
  if (
    skewed.running
    && nextTotal <= 1
    && prevTotal > 15
    && (prevClock.running || skewed.running)
  ) {
    return {
      ...prevClock,
      running: true,
      server_now: skewed.server_now || prevClock.server_now,
    };
  }

  return skewed;
};

/** Локальный тик clock.display пока running. */
export const tickTabloClock = (clock, elapsedMs = 1000) => {
  if (!clock || typeof clock !== 'object') return clock;
  if (!clock.running) {
    return {
      ...clock,
      display: formatTabloClockDisplay(clock.minute, clock.second),
    };
  }
  const total = (Number(clock.minute) || 0) * 60 + (Number(clock.second) || 0) + Math.floor(elapsedMs / 1000);
  const minute = Math.max(0, Math.floor(total / 60));
  const second = Math.max(0, total % 60);
  return {
    ...clock,
    minute,
    second,
    display: formatTabloClockDisplay(minute, second),
  };
};

export const tabloTeamLogo = (image) => getTeamImageUrl(image) || '';

let sharedSocket = null;

export const getTabloSocket = () => {
  if (sharedSocket) return sharedSocket;
  const url = resolveTabloSocketUrl();
  sharedSocket = io(url || undefined, {
    transports: ['websocket', 'polling'],
    autoConnect: true,
  });
  return sharedSocket;
};

export const subscribeTablo = ({ overlayKey, matchId, onUpdate, onAck } = {}) => {
  const socket = getTabloSocket();
  const handler = (payload) => {
    const state = extractTabloState(payload) || payload;
    if (typeof onUpdate === 'function') onUpdate(state);
  };
  socket.on('tablo:update', handler);

  const joinPayload = overlayKey
    ? { overlay_key: String(overlayKey) }
    : { match_id: Number(matchId) };

  const doJoin = () => {
    socket.emit('joinTablo', joinPayload, (ack) => {
      if (typeof onAck === 'function') onAck(ack);
      if (ack && ack.ok === false && (ack.closed || ack.err === 'not_found')) {
        if (typeof onUpdate === 'function') {
          onUpdate({
            closed: true,
            reason: 'closed',
            match_id: Number(matchId) || null,
            overlay_key: overlayKey || null,
          });
        }
        return;
      }
      // После reconnect ack часто несёт свежий snapshot — дотягиваем state/clock
      // сразу, не дожидаясь следующего tablo:update.
      if (ack && typeof onUpdate === 'function' && (ack.clock || ack.match || ack.layers)) {
        const state = extractTabloState(ack) || ack;
        onUpdate(state);
      }
    });
  };

  // Важно: join на каждый connect (в т.ч. reconnect). once() оставлял
  // overlay вне комнаты tablo:{matchId} после тихого disconnect.
  socket.on('connect', doJoin);
  if (socket.connected) doJoin();

  return () => {
    socket.off('connect', doJoin);
    socket.off('tablo:update', handler);
    const leaveId = Number(matchId || joinPayload.match_id);
    if (Number.isFinite(leaveId) && leaveId > 0) {
      socket.emit('leaveTablo', { match_id: leaveId });
    }
  };
};
